import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Card } from '@/components/Card/Card';

import { ChatErrorBanner } from './ChatErrorBanner';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { MessageList } from './MessageList';
import { ResetConversationDialog } from './ResetConversationDialog';
import { useChatPanelSize } from './useChatPanelSize';
import type { ChatSession } from './useChatSession';
import styles from './ChatPanel.module.css';

interface ChatPanelProps {
  avatar?: ReactNode;
  // Exact URLs of this chapter's figures — the renderer's image allowlist.
  chapterImageUrls?: ReadonlySet<string>;
  isClosing?: boolean;
  onClose: () => void;
  onTypingChange?: (isTyping: boolean) => void;
  session: ChatSession;
}

export function ChatPanel({
  avatar,
  chapterImageUrls,
  isClosing = false,
  onClose,
  onTypingChange,
  session,
}: ChatPanelProps) {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const { canResize, size, toggleSize } = useChatPanelSize();

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Card
      aria-label="AI chat"
      as="aside"
      className={styles.panel}
      data-closing={isClosing ? 'true' : 'false'}
      data-open={isClosing ? 'false' : 'true'}
      data-size={size}
      variant="elevated"
    >
      <ChatHeader
        canReset={session.messages.length > 0}
        canResize={canResize}
        onClose={onClose}
        onReset={() => setIsResetDialogOpen(true)}
        onToggleSize={toggleSize}
        size={size}
      />

      {session.error ? (
        <ChatErrorBanner error={session.error} onDismiss={session.clearError} />
      ) : null}

      <MessageList
        chapterImageUrls={chapterImageUrls}
        isLoading={session.isLoading}
        messages={session.messages}
        onRetryMessage={session.retryMessage}
      />

      <ChatInput
        avatar={avatar}
        disabled={session.isLoading}
        onSend={session.sendMessage}
        onTypingChange={onTypingChange}
      />

      <ResetConversationDialog
        isResetting={session.isResetting}
        onConfirm={() => {
          void session.resetConversation().then(() => {
            setIsResetDialogOpen(false);
          });
        }}
        onOpenChange={setIsResetDialogOpen}
        open={isResetDialogOpen}
      />
    </Card>
  );
}
